import React from 'react';
import { LABELS } from './model';

const labels = { cpcBidMicros: 'Group CPC bid (micros)', keywords: 'Keywords', negativeKeywords: 'Proposed negatives', match_type: 'Match type', keyword: 'Keyword', selected: 'Include ad group', name: 'Name', rationale: 'Reason', inputs: 'Audience inputs', included: 'Include input', kind: 'Audience type', value: 'Audience / segment name', selectedId: 'Google audience', members: 'Exact inputs', segmentType: 'Interpretation', memberKind: 'Input category', ads: 'Existing ads', expansion: 'Optimized targeting', targeting: 'Demographics' };
const types = { interest: 'Interest / in-market', custom_audience: 'Existing custom segment', new_custom_segment: 'New custom segment', SEARCH: 'Search activity', AUTO: 'Interests / purchase intent' };
const label = key => labels[key] || LABELS[key] || key;
function display(value, key, catalog) {
  if (value == null) return 'Not set';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? value.map(v => display(v, key, catalog)).join('\n') : 'None';
  if (key === 'ads') return `${catalog?.ads?.find(ad => ad.resource === value)?.content?.name || 'Ad'} (${value})`;
  if (key === 'selectedId') {
    const match = [...(catalog?.interests || []), ...(catalog?.custom_audiences || [])].find(a => a.id === value);
    return `${match?.name || 'Audience'} (${value})`;
  }
  if (typeof value === 'object' && 'keyword' in value) return `${value.keyword} (${value.match_type || 'Not set'})`;
  return LABELS[value] || types[value] || String(value);
}

// Compare payloads, not the model's prose. Stable group IDs keep a rename from
// looking like removal; inputs have no IDs and are compared by displayed position.
export function proposalChanges(before, after, catalog) {
  const changes = [];
  function walk(a, b, path, key) {
    if (JSON.stringify(a) === JSON.stringify(b)) return;
    if (key === 'inputs') {
      for (let i = 0; i < Math.max(a?.length || 0, b?.length || 0); i++) walk(a?.[i], b?.[i], `${path} / Input ${i + 1}`, 'input');
    } else if ((a && !Array.isArray(a) && typeof a === 'object') || (b && !Array.isArray(b) && typeof b === 'object')) {
      for (const field of new Set([...Object.keys(a || {}), ...Object.keys(b || {})])) {
        if (field !== 'id') walk(a?.[field], b?.[field], `${path} / ${label(field)}`, field);
      }
    } else changes.push({ field: path, before: display(a, key, catalog), after: display(b, key, catalog) });
  }
  for (const key of new Set([...Object.keys(before || {}), ...Object.keys(after || {})])) {
    if (key !== 'tests') walk(before?.[key], after?.[key], label(key), key);
  }
  const oldGroups = before?.tests || [], newGroups = after?.tests || [];
  for (const id of new Set([...oldGroups, ...newGroups].map(t => t.id))) {
    const a = oldGroups.find(t => t.id === id), b = newGroups.find(t => t.id === id);
    const name = b?.name || a?.name || 'Unnamed ad group';
    if (!a || !b) changes.push({ field: name, before: a ? 'In draft' : 'Not in draft', after: b ? 'Added to draft' : 'Removed from draft' });
    walk(a, b, name, 'group');
  }
  return changes;
}

export function proposalSummary(before, after) {
  const oldGroups = before?.tests || [], newGroups = after?.tests || [];
  const rows = [];
  for (const id of new Set([...oldGroups, ...newGroups].map(t => t.id))) {
    const a = oldGroups.find(t => t.id === id), b = newGroups.find(t => t.id === id);
    if (JSON.stringify(a) === JSON.stringify(b)) continue;
    const changed = [];
    if (a && b) {
      if (a.cpcBidMicros !== b.cpcBidMicros) changed.push('group CPC bid');
      if (a.name !== b.name) changed.push('name');
      if (JSON.stringify(a.keywords) !== JSON.stringify(b.keywords)) changed.push('keywords / match types');
      if (JSON.stringify(a.negativeKeywords) !== JSON.stringify(b.negativeKeywords)) changed.push('proposed negatives');
      if (JSON.stringify(a.inputs) !== JSON.stringify(b.inputs)) changed.push('targeting');
      if (JSON.stringify(a.targeting) !== JSON.stringify(b.targeting)) changed.push('demographics');
      if (JSON.stringify(a.ads) !== JSON.stringify(b.ads)) changed.push('ad selection');
      if (a.rationale !== b.rationale) changed.push('rationale');
    }
    const state = t => !t ? 'Not in draft' : t.selected ? 'Included' : 'Excluded';
    rows.push({ name: b?.name || a?.name || 'Unnamed ad group', before: state(a), after: state(b), detail: !a ? 'New group — review targeting, demographics and ads below.' : !b ? 'Removed from draft.' : changed.length ? `Changed: ${changed.join(', ')}.` : 'Inclusion changed.' });
  }
  return rows;
}

export default function ProposalPreview({ changes, before, after }) {
  const summary = proposalSummary(before, after);
  const globalChanges = changes.filter(change => ![...(before?.tests || []), ...(after?.tests || [])].some(t => change.field === t.name || change.field.startsWith(`${t.name} / `)));
  return <section aria-label="Proposal change preview">
    <h3>Proposed changes</h3>
    <p>Before is the draft you submitted. After is the assistant proposal. Nothing has been applied yet.</p>
    {!!summary.length && <ul aria-label="Changes at a glance">{summary.map((row, i) => <li key={i}><strong>{row.name}</strong>: {row.before} → {row.after}<p>{row.detail}</p></li>)}</ul>}
    {before && after && !!globalChanges.length && <p>Other settings changed: {[...new Set(globalChanges.map(change => change.field))].join(', ')}.</p>}
    <details><summary>{changes.length} setting changes — review before applying</summary>
      <div className="ow-assistant-table" tabIndex={0}><table>
        <thead><tr><th>Setting</th><th>Before</th><th>After</th></tr></thead>
        <tbody>{changes.map((change, i) => <tr key={i}><th scope="row">{change.field}</th><td style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{change.before}</td><td style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{change.after}</td></tr>)}</tbody>
      </table></div>
    </details>
  </section>;
}
